import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '../components/layout/DashboardLayout';
import { EmptyState } from '@/components/shared/EmptyState';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatCurrency, cn } from '@/lib/utils';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import {
  Search,
  Filter,
  Plus,
  Clock,
  User,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Circle,
  Pause,
  LayoutList,
  LayoutGrid,
  ClipboardList,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const priorityColors = {
  critical: 'bg-red-100 text-red-700 border-red-300',
  high: 'bg-orange-100 text-orange-700 border-orange-300',
  medium: 'bg-amber-100 text-amber-700 border-amber-300',
  low: 'bg-slate-100 text-slate-600 border-slate-300',
};

const statusColors = {
  pending: 'bg-slate-100 text-slate-600 border-slate-300',
  scheduled: 'bg-amber-100 text-amber-700 border-amber-300',
  in_progress: 'bg-sky-100 text-sky-700 border-sky-300',
  on_hold: 'bg-purple-100 text-purple-700 border-purple-300',
  completed: 'bg-emerald-100 text-emerald-700 border-emerald-300',
};

const statusIcons = {
  pending: Circle,
  scheduled: Clock,
  in_progress: AlertTriangle,
  on_hold: Pause,
  completed: CheckCircle,
};

export default function WorkOrdersPage() {
  const { hasPermission, hasRole } = useAuth();
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [viewMode, setViewMode] = useState('list');
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [activeWO, setActiveWO] = useState(null);

  const [form, setForm] = useState({
    title: '',
    description: '',
    work_order_type: 'maintenance',
    priority: 'medium',
    assigned_to: '',
    due_date: '',
    estimated_hours: '',
    related_action_plan_ids: '',
    revenue_at_risk: '',
  });

  useEffect(() => {
    fetchWorkOrders();
  }, []);

  const fetchWorkOrders = async () => {
    try {
      const response = await axios.get(`${API}/work-orders`);
      setWorkOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch work orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => setForm({
    title: '', description: '', work_order_type: 'maintenance', priority: 'medium', assigned_to: '', due_date: '', estimated_hours: '', related_action_plan_ids: '', revenue_at_risk: ''
  });

  const openCreate = () => {
    resetForm();
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    try {
      const payload = {
        title: form.title,
        description: form.description,
        work_order_type: form.work_order_type,
        priority: form.priority,
        assigned_to: form.assigned_to.split(',').map(s => s.trim()).filter(Boolean),
        due_date: form.due_date,
        estimated_hours: parseFloat(form.estimated_hours) || undefined,
        related_action_plan_ids: form.related_action_plan_ids.split(',').map(s => s.trim()).filter(Boolean),
        revenue_at_risk: parseFloat(form.revenue_at_risk) || 0,
      };
      await axios.post(`${API}/work-orders`, payload);
      setCreateOpen(false);
      toast({ title: 'Work order created', description: 'New work order has been created.' });
      fetchWorkOrders();
    } catch (err) {
      console.error('Failed to create work order', err);
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to create work order' });
    }
  };

  const openEdit = (wo) => {
    setActiveWO(wo);
    setForm({
      title: wo.title || '',
      description: wo.description || '',
      work_order_type: wo.work_order_type || 'maintenance',
      priority: wo.priority || 'medium',
      assigned_to: Array.isArray(wo.assigned_to) ? wo.assigned_to.join(', ') : (wo.assigned_to || ''),
      due_date: wo.due_date || '',
      estimated_hours: wo.estimated_hours || '',
      related_action_plan_ids: (wo.related_action_plan_ids || []).join(', '),
      revenue_at_risk: wo.revenue_at_risk || '',
    });
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      const payload = {
        title: form.title,
        description: form.description,
        work_order_type: form.work_order_type,
        priority: form.priority,
        assigned_to: form.assigned_to.split(',').map(s => s.trim()).filter(Boolean),
        due_date: form.due_date,
        estimated_hours: parseFloat(form.estimated_hours) || undefined,
        related_action_plan_ids: form.related_action_plan_ids.split(',').map(s => s.trim()).filter(Boolean),
        revenue_at_risk: parseFloat(form.revenue_at_risk) || 0,
      };
      await axios.patch(`${API}/work-orders/${activeWO.id}`, payload);
      setEditOpen(false);
      toast({ title: 'Work order updated', description: 'Changes saved.' });
      fetchWorkOrders();
    } catch (err) {
      console.error('Failed to update work order', err);
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to update work order' });
    }
  };

  const openDelete = (wo) => {
    setActiveWO(wo);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/work-orders/${activeWO.id}`);
      setDeleteOpen(false);
      toast({ title: 'Work order deleted', description: 'Work order was cancelled.' });
      fetchWorkOrders();
    } catch (err) {
      console.error('Failed to delete work order', err);
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to delete work order' });
    }
  };

  const filteredWorkOrders = workOrders.filter((wo) => {
    const matchesSearch =
      searchQuery === '' ||
      wo.wo_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
      wo.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      wo.asset_name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || wo.status === statusFilter;
    const matchesPriority = priorityFilter === 'all' || wo.priority === priorityFilter;
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const getProgress = (wo) => {
    const completed = wo.tasks?.filter((t) => t.completed).length || 0;
    const total = wo.tasks?.length || 1;
    return Math.round((completed / total) * 100);
  };

  // Group work orders by status for Kanban view
  const kanbanColumns = {
    pending: filteredWorkOrders.filter((wo) => wo.status === 'pending'),
    scheduled: filteredWorkOrders.filter((wo) => wo.status === 'scheduled'),
    in_progress: filteredWorkOrders.filter((wo) => wo.status === 'in_progress'),
    on_hold: filteredWorkOrders.filter((wo) => wo.status === 'on_hold'),
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">Work Orders</h1>
            <p className="text-slate-500">{filteredWorkOrders.length} work orders</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center bg-slate-100 rounded-lg p-1">
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                data-testid="view-list"
              >
                <LayoutList className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'kanban' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('kanban')}
                data-testid="view-kanban"
              >
                <LayoutGrid className="w-4 h-4" />
              </Button>
            </div>
            <Button data-testid="create-workorder" onClick={openCreate}>
              <Plus className="w-4 h-4 mr-2" />
              New Work Order
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by WO number, title, or asset..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="wo-search"
                />
              </div>
              <div className="flex gap-3">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[150px]" data-testid="status-filter">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="on_hold">On Hold</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                  <SelectTrigger className="w-[150px]" data-testid="priority-filter">
                    <SelectValue placeholder="Priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Priority</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* List View */}
        {viewMode === 'list' && (
          <Card className="border-slate-200" data-testid="wo-list">
            <CardContent className="p-0">
              {filteredWorkOrders.length === 0 ? (
                <EmptyState
                  icon={ClipboardList}
                  title="No work orders"
                  description={searchQuery ? "Try adjusting your search or filters." : "Create a work order from an alert, asset, or action plan."}
                  actionLabel={!searchQuery ? "Create work order" : undefined}
                  onAction={!searchQuery ? openCreate : undefined}
                />
              ) : (
                <div className="divide-y divide-slate-100">
                  {filteredWorkOrders.map((wo) => {
                    const progress = getProgress(wo);
                    const StatusIcon = statusIcons[wo.status] || Circle;

                    return (
                      <div
                        key={wo.id}
                        className="p-4 hover:bg-slate-50 transition-colors cursor-pointer"
                        data-testid={`wo-${wo.wo_number}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-4">
                            <div
                              className={cn(
                                'w-10 h-10 rounded-lg flex items-center justify-center',
                                wo.priority === 'critical'
                                  ? 'bg-red-100'
                                  : wo.priority === 'high'
                                  ? 'bg-orange-100'
                                  : 'bg-slate-100'
                              )}
                            >
                              <StatusIcon
                                className={cn(
                                  'w-5 h-5',
                                  wo.priority === 'critical'
                                    ? 'text-red-600'
                                    : wo.priority === 'high'
                                    ? 'text-orange-600'
                                    : 'text-slate-600'
                                )}
                              />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-sm font-semibold text-slate-900">{wo.wo_number}</span>
                                <Badge variant="outline" className={cn('text-xs', priorityColors[wo.priority])}>
                                  {wo.priority.toUpperCase()}
                                </Badge>
                                <Badge variant="outline" className={cn('text-xs', statusColors[wo.status])}>
                                  {wo.status.replace('_', ' ').toUpperCase()}
                                </Badge>
                              </div>
                              <p className="font-medium text-slate-900 mt-1">{wo.title}</p>
                              <p className="text-sm text-slate-500">
                                {wo.asset_name} • {wo.site}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-red-600">
                              {formatCurrency(wo.revenue_at_risk)} at risk
                            </p>
                            <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
                              <User className="w-4 h-4" />
                              {(Array.isArray(wo.assigned_to) ? wo.assigned_to.join(', ') : (wo.assigned_to || 'Unassigned'))}
                            </div>
                            <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
                              <Calendar className="w-4 h-4" />
                              Due: {wo.due_date}
                            </div>
                          </div>
                        </div>
                          <div className="mt-4 flex items-center gap-4">
                          <div className="flex-1">
                            <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                              <span>
                                {wo.tasks?.filter((t) => t.completed).length || 0}/{wo.tasks?.length || 0} tasks
                              </span>
                              <span>{progress}%</span>
                            </div>
                            <Progress value={progress} className="h-1.5" />
                          </div>
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={() => openEdit(wo)}>
                              View / Edit
                            </Button>
                            <Button variant="destructive" size="sm" onClick={() => openDelete(wo)}>
                              Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Kanban View */}
        {viewMode === 'kanban' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="wo-kanban">
            {Object.entries(kanbanColumns).map(([status, wos]) => (
              <div key={status} className="space-y-3">
                <div className="flex items-center justify-between p-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={cn(statusColors[status])}>
                      {status.replace('_', ' ').toUpperCase()}
                    </Badge>
                    <span className="text-sm text-slate-500">{wos.length}</span>
                  </div>
                </div>
                <div className="space-y-3 min-h-[200px] p-2 bg-slate-100/50 rounded-lg">
                  {wos.map((wo) => {
                    const progress = getProgress(wo);

                    return (
                      <Card
                        key={wo.id}
                        className="border-slate-200 cursor-pointer hover:shadow-md transition-shadow"
                        data-testid={`kanban-${wo.wo_number}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-mono text-xs font-semibold text-slate-500">{wo.wo_number}</span>
                            <Badge variant="outline" className={cn('text-xs', priorityColors[wo.priority])}>
                              {wo.priority.toUpperCase()}
                            </Badge>
                          </div>
                          <p className="font-medium text-slate-900 text-sm line-clamp-2">{wo.title}</p>
                          <p className="text-xs text-slate-500 mt-1">{wo.asset_name}</p>
                          <div className="mt-3">
                            <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                              <span>
                                {wo.tasks?.filter((t) => t.completed).length || 0}/{wo.tasks?.length || 0} tasks
                              </span>
                              <span>{progress}%</span>
                            </div>
                            <Progress value={progress} className="h-1" />
                          </div>
                            <div className="mt-3 flex items-center justify-between">
                            <div className="flex items-center gap-1 text-xs text-slate-500">
                              <User className="w-3 h-3" />
                              {(Array.isArray(wo.assigned_to) ? (wo.assigned_to[0] || 'Unassigned') : (wo.assigned_to ? wo.assigned_to.split(' ')[0] : 'Unassigned'))}
                            </div>
                            <span className="text-xs font-semibold text-red-600">
                              {formatCurrency(wo.revenue_at_risk)}
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create Dialog */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>New Work Order</DialogTitle>
              <DialogDescription>Create a new work order and assign it.</DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Title</Label>
                <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} />
              </div>
              <div className="grid gap-2">
                <Label>Description</Label>
                <Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} rows={4} />
              </div>
              <div className="grid gap-2 md:grid-cols-2 md:grid">
                <div>
                  <Label>Type</Label>
                  <Select value={form.work_order_type} onValueChange={(v) => setForm({...form, work_order_type: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="maintenance">Maintenance</SelectItem>
                      <SelectItem value="corrective">Corrective</SelectItem>
                      <SelectItem value="inspection">Inspection</SelectItem>
                      <SelectItem value="installation">Installation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({...form, priority: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="critical">Critical</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid gap-2 md:grid-cols-2 md:grid">
                <div>
                  <Label>Assigned To</Label>
                  <Input value={form.assigned_to} onChange={(e) => setForm({...form, assigned_to: e.target.value})} placeholder="Comma separated user ids or names" />
                </div>
                <div>
                  <Label>Due Date</Label>
                  <Input type="date" value={form.due_date} onChange={(e) => setForm({...form, due_date: e.target.value})} />
                </div>
              </div>
              <div className="grid gap-2 md:grid-cols-2 md:grid">
                <div>
                  <Label>Estimated Hours</Label>
                  <Input value={form.estimated_hours} onChange={(e) => setForm({...form, estimated_hours: e.target.value})} />
                </div>
                <div>
                  <Label>Revenue at Risk</Label>
                  <Input value={form.revenue_at_risk} onChange={(e) => setForm({...form, revenue_at_risk: e.target.value})} />
                </div>
              </div>
              <div className="grid gap-2">
                <Label>Related Action Plans</Label>
                <Input value={form.related_action_plan_ids} onChange={(e) => setForm({...form, related_action_plan_ids: e.target.value})} placeholder="Comma separated action plan ids" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button onClick={handleCreate}>Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>Edit Work Order</DialogTitle>
              <DialogDescription>Update work order details.</DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Title</Label>
                <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} />
              </div>
              <div className="grid gap-2">
                <Label>Description</Label>
                <Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} rows={4} />
              </div>
              <div className="grid gap-2 md:grid-cols-2 md:grid">
                <div>
                  <Label>Type</Label>
                  <Select value={form.work_order_type} onValueChange={(v) => setForm({...form, work_order_type: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="maintenance">Maintenance</SelectItem>
                      <SelectItem value="corrective">Corrective</SelectItem>
                      <SelectItem value="inspection">Inspection</SelectItem>
                      <SelectItem value="installation">Installation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({...form, priority: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="critical">Critical</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid gap-2 md:grid-cols-2 md:grid">
                <div>
                  <Label>Assigned To</Label>
                  <Input value={form.assigned_to} onChange={(e) => setForm({...form, assigned_to: e.target.value})} placeholder="Comma separated user ids or names" />
                </div>
                <div>
                  <Label>Due Date</Label>
                  <Input type="date" value={form.due_date} onChange={(e) => setForm({...form, due_date: e.target.value})} />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveEdit}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
          <DialogContent className="sm:max-w-[420px]">
            <DialogHeader>
              <DialogTitle>Delete Work Order</DialogTitle>
              <DialogDescription>This will cancel the work order and mark it as deleted. Continue?</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={confirmDelete}>Delete</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
